import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { shelfAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { cn } from '../lib/utils';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'sonner';
import {
    Activity, Clock, CheckCircle, BookOpen, ChevronRight,
    Trophy, AlertTriangle, Loader2, RotateCcw, ArrowLeft,
    GraduationCap, Stethoscope, BarChart3
} from 'lucide-react';
import Layout from '../components/Layout';

const SUBJECT_COLORS = {
    'Medicine':                 { bg: 'bg-blue-600',    light: 'bg-blue-50',    border: 'border-blue-200',    text: 'text-blue-700'    },
    'Surgery':                  { bg: 'bg-slate-600',   light: 'bg-slate-50',   border: 'border-slate-200',   text: 'text-slate-700'   },
    'Pediatrics':               { bg: 'bg-amber-500',   light: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-700'   },
    'Obstetrics & Gynecology':  { bg: 'bg-pink-500',    light: 'bg-pink-50',    border: 'border-pink-200',    text: 'text-pink-700'    },
    'Family Medicine':          { bg: 'bg-green-600',   light: 'bg-green-50',   border: 'border-green-200',   text: 'text-green-700'   },
    'Psychiatry':               { bg: 'bg-violet-600',  light: 'bg-violet-50',  border: 'border-violet-200',  text: 'text-violet-700'  },
    'Neurology':                { bg: 'bg-indigo-600',  light: 'bg-indigo-50',  border: 'border-indigo-200',  text: 'text-indigo-700'  },
    'Emergency Medicine':       { bg: 'bg-red-600',     light: 'bg-red-50',     border: 'border-red-200',     text: 'text-red-700'     },
};

const SYSTEM_COLORS = {
    'Cardiovascular':          { bg: 'bg-red-500',     light: 'bg-red-50',     border: 'border-red-200',     text: 'text-red-700'     },
    'Respiratory':             { bg: 'bg-sky-500',     light: 'bg-sky-50',     border: 'border-sky-200',     text: 'text-sky-700'     },
    'Gastrointestinal':        { bg: 'bg-amber-500',   light: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-700'   },
    'Renal/Urinary':           { bg: 'bg-cyan-500',    light: 'bg-cyan-50',    border: 'border-cyan-200',    text: 'text-cyan-700'    },
    'Endocrine':               { bg: 'bg-purple-500',  light: 'bg-purple-50',  border: 'border-purple-200',  text: 'text-purple-700'  },
    'Exocrine':                { bg: 'bg-lime-500',    light: 'bg-lime-50',    border: 'border-lime-200',    text: 'text-lime-700'    },
    'Reproductive & Endocrine':{ bg: 'bg-pink-500',    light: 'bg-pink-50',    border: 'border-pink-200',    text: 'text-pink-700'    },
    'Musculoskeletal':         { bg: 'bg-orange-500',  light: 'bg-orange-50',  border: 'border-orange-200',  text: 'text-orange-700'  },
    'Nervous System':          { bg: 'bg-indigo-500',  light: 'bg-indigo-50',  border: 'border-indigo-200',  text: 'text-indigo-700'  },
    'Hematologic':             { bg: 'bg-rose-500',    light: 'bg-rose-50',    border: 'border-rose-200',    text: 'text-rose-700'    },
    'Immunology':              { bg: 'bg-teal-500',    light: 'bg-teal-50',    border: 'border-teal-200',    text: 'text-teal-700'    },
    'Skin & Connective Tissue':{ bg: 'bg-lime-600',    light: 'bg-lime-50',    border: 'border-lime-200',    text: 'text-lime-700'    },
    'Behavioral/Psychiatry':   { bg: 'bg-violet-500',  light: 'bg-violet-50',  border: 'border-violet-200',  text: 'text-violet-700'  },
    'Hepatobiliary':           { bg: 'bg-emerald-500', light: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700' },
    'Ophthalmology':           { bg: 'bg-blue-500',    light: 'bg-blue-50',    border: 'border-blue-200',    text: 'text-blue-700'    },
    'ENT/Head & Neck':         { bg: 'bg-fuchsia-500', light: 'bg-fuchsia-50', border: 'border-fuchsia-200', text: 'text-fuchsia-700' },
    'Lymphatic':               { bg: 'bg-green-500',   light: 'bg-green-50',   border: 'border-green-200',   text: 'text-green-700'   },
};

const PRECLINICAL_COLORS = {
    'Behavioral Science':             { bg: 'bg-teal-600',    light: 'bg-teal-50',    border: 'border-teal-200',    text: 'text-teal-700'    },
    'Biochemistry':                   { bg: 'bg-amber-600',   light: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-700'   },
    'Gross Anatomy':                  { bg: 'bg-orange-600',  light: 'bg-orange-50',  border: 'border-orange-200',  text: 'text-orange-700'  },
    'Gross Anatomy + Embryology':     { bg: 'bg-rose-600',    light: 'bg-rose-50',    border: 'border-rose-200',    text: 'text-rose-700'    },
    'Histology':                      { bg: 'bg-pink-600',    light: 'bg-pink-50',    border: 'border-pink-200',    text: 'text-pink-700'    },
    'Microbiology':                   { bg: 'bg-green-700',   light: 'bg-green-50',   border: 'border-green-200',   text: 'text-green-700'   },
    'Microbiology + Immunology':      { bg: 'bg-emerald-600', light: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700' },
    'Neuroscience':                   { bg: 'bg-indigo-700',  light: 'bg-indigo-50',  border: 'border-indigo-200',  text: 'text-indigo-700'  },
    'Pathology':                      { bg: 'bg-red-700',     light: 'bg-red-50',     border: 'border-red-200',     text: 'text-red-700'     },
    'Pharmacology':                   { bg: 'bg-cyan-700',    light: 'bg-cyan-50',    border: 'border-cyan-200',    text: 'text-cyan-700'    },
    'Physiology':                     { bg: 'bg-blue-700',    light: 'bg-blue-50',    border: 'border-blue-200',    text: 'text-blue-700'    },
    'Introduction to Clinical Diagnosis': { bg: 'bg-slate-600', light: 'bg-slate-50', border: 'border-slate-200',  text: 'text-slate-700'   },
};

function getColor(name, examType) {
    if (examType === 'subject') return SUBJECT_COLORS[name] || { bg: 'bg-slate-500', light: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700' };
    if (examType === 'preclinical') return PRECLINICAL_COLORS[name] || { bg: 'bg-slate-500', light: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700' };
    return SYSTEM_COLORS[name] || { bg: 'bg-slate-500', light: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700' };
}

function formatTime(seconds) {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
}

// ── Exam Card ─────────────────────────────────────────────────────────────────
function ExamCard({ exam, isSelected, onSelect, examType, isDark }) {
    const c = getColor(exam.name, examType);
    return (
        <button
            onClick={() => onSelect(exam.name)}
            className={cn(
                "rounded-xl border-2 p-4 text-left transition-all hover:scale-[1.02] w-full",
                isSelected
                    ? `${c.bg} border-transparent text-white shadow-lg`
                    : isDark
                        ? "border-slate-700 bg-slate-800 text-slate-200 hover:border-slate-500"
                        : `${c.border} ${c.light} hover:shadow-md`
            )}
            data-testid={`exam-${exam.id}`}
        >
            {examType === 'subject'
                ? <GraduationCap className="h-4 w-4 mb-2 opacity-80" />
                : <Activity className="h-4 w-4 mb-2 opacity-80" />
            }
            <p className={cn("font-semibold text-sm leading-tight", !isSelected && (isDark ? "text-slate-200" : c.text))}>
                {exam.name}
            </p>
            {exam.description && (
                <p className={cn("text-xs mt-1.5 leading-snug line-clamp-2", isSelected ? "text-white/70" : "opacity-55")}>
                    {exam.description}
                </p>
            )}
        </button>
    );
}

// ── Setup Screen ───────────────────────────────────────────────────────────────
function SetupScreen({ onStart, isDark }) {
    const [examData, setExamData] = useState({ subject_exams: [], organ_systems: [] });
    const [examType, setExamType] = useState('subject'); // 'subject' | 'preclinical' | 'system'
    const [selected, setSelected] = useState(null);
    const [questionCount, setQuestionCount] = useState(40);
    const [timeLimit, setTimeLimit] = useState(60);
    const [loading, setLoading] = useState(false);
    const [loadingSystems, setLoadingSystems] = useState(true);

    useEffect(() => {
        shelfAPI.getSystems()
            .then(r => setExamData(r.data))
            .catch(() => toast.error('Failed to load exams'))
            .finally(() => setLoadingSystems(false));
    }, []);

    // Reset selection when tab changes
    const handleTabChange = (val) => {
        setExamType(val);
        setSelected(null);
    };

    const handleStart = async () => {
        if (!selected) { toast.error('Please select an exam'); return; }
        setLoading(true);
        try {
            const res = await shelfAPI.startExam(selected, questionCount, timeLimit, examType);
            onStart(res.data.session_id, selected, questionCount, res.data.time_limit_seconds, examType);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to start exam');
        } finally {
            setLoading(false);
        }
    };

    const currentList = examType === 'subject'
        ? examData.subject_exams
        : examType === 'preclinical'
            ? examData.preclinical_exams
            : examData.organ_systems;
    const colors = selected ? getColor(selected, examType) : null;

    return (
        <div className="max-w-5xl mx-auto space-y-7">
            {/* Header */}
            <div>
                <h1 className={cn("text-3xl md:text-4xl font-bold mb-2", isDark ? "text-white" : "text-slate-900")} style={{ fontFamily: 'Manrope' }}>
                    Shelf Exam
                </h1>
                <p className={cn("text-base", isDark ? "text-slate-300" : "text-slate-600")}>
                    Timed practice simulating official NBME Subject &amp; Organ System Exams
                </p>
            </div>

            {/* Tabs: By Subject / By System */}
            <Tabs value={examType} onValueChange={handleTabChange}>
                <TabsList className={cn("mb-5", isDark && "bg-slate-800")}>
                    <TabsTrigger value="subject" className="gap-2" data-testid="tab-subject">
                        <GraduationCap className="h-4 w-4" />
                        Clinical Subject Exams
                    </TabsTrigger>
                    <TabsTrigger value="preclinical" className="gap-2" data-testid="tab-preclinical">
                        <Stethoscope className="h-4 w-4" />
                        Pre-Clinical Subject Exams
                    </TabsTrigger>
                    <TabsTrigger value="system" className="gap-2" data-testid="tab-system">
                        <Activity className="h-4 w-4" />
                        Organ System Exams
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="subject">
                    <p className={cn("text-xs mb-3 font-medium uppercase tracking-wide", isDark ? "text-slate-400" : "text-slate-500")}>
                        8 NBME Clinical Clerkship Shelf Exams
                    </p>
                    {loadingSystems ? (
                        <div className="flex items-center gap-2 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" />Loading...</div>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                            {(examData.subject_exams || []).map(exam => (
                                <ExamCard key={exam.id} exam={exam} isSelected={selected === exam.name} onSelect={setSelected} examType="subject" isDark={isDark} />
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="preclinical">
                    <p className={cn("text-xs mb-3 font-medium uppercase tracking-wide", isDark ? "text-slate-400" : "text-slate-500")}>
                        12 NBME Basic Science Subject Exams
                    </p>
                    {loadingSystems ? (
                        <div className="flex items-center gap-2 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" />Loading...</div>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                            {(examData.preclinical_exams || []).map(exam => (
                                <ExamCard key={exam.id} exam={exam} isSelected={selected === exam.name} onSelect={setSelected} examType="preclinical" isDark={isDark} />
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="system">
                    <p className={cn("text-xs mb-3 font-medium uppercase tracking-wide", isDark ? "text-slate-400" : "text-slate-500")}>
                        12 NBME Organ System Exams
                    </p>
                    {loadingSystems ? (
                        <div className="flex items-center gap-2 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" />Loading...</div>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                            {(examData.organ_systems || []).map(exam => (
                                <ExamCard key={exam.id} exam={exam} isSelected={selected === exam.name} onSelect={setSelected} examType="system" isDark={isDark} />
                            ))}
                        </div>
                    )}
                </TabsContent>
            </Tabs>

            {/* Config */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                    <p className={cn("text-sm font-semibold mb-2", isDark ? "text-slate-300" : "text-slate-700")}>Number of Questions</p>
                    <div className="flex gap-2 flex-wrap">
                        {[20, 40, 60, 80].map(n => (
                            <button
                                key={n}
                                onClick={() => setQuestionCount(n)}
                                className={cn(
                                    "px-4 py-2 rounded-lg border-2 text-sm font-medium transition-colors",
                                    questionCount === n
                                        ? "bg-indigo-600 border-indigo-600 text-white"
                                        : isDark ? "border-slate-600 text-slate-300 hover:border-slate-400" : "border-slate-300 text-slate-700 hover:border-indigo-400"
                                )}
                                data-testid={`questions-${n}`}
                            >{n}</button>
                        ))}
                    </div>
                </div>
                <div>
                    <p className={cn("text-sm font-semibold mb-2", isDark ? "text-slate-300" : "text-slate-700")}>Time Limit</p>
                    <div className="flex gap-2 flex-wrap">
                        {[30, 60, 90, 120].map(t => (
                            <button
                                key={t}
                                onClick={() => setTimeLimit(t)}
                                className={cn(
                                    "px-4 py-2 rounded-lg border-2 text-sm font-medium transition-colors",
                                    timeLimit === t
                                        ? "bg-indigo-600 border-indigo-600 text-white"
                                        : isDark ? "border-slate-600 text-slate-300 hover:border-slate-400" : "border-slate-300 text-slate-700 hover:border-indigo-400"
                                )}
                                data-testid={`time-${t}`}
                            >{t}m</button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Summary */}
            {selected && colors && (
                <div className={cn(
                    "p-4 rounded-xl border flex items-center gap-3",
                    isDark ? "bg-slate-800 border-slate-700" : `${colors.light} ${colors.border}`
                )}>
                    {examType === 'subject'
                        ? <GraduationCap className={cn("h-5 w-5 flex-shrink-0", isDark ? "text-slate-300" : colors.text)} />
                        : <Activity className={cn("h-5 w-5 flex-shrink-0", isDark ? "text-slate-300" : colors.text)} />
                    }
                    <p className={cn("text-sm", isDark ? "text-slate-300" : colors.text)}>
                        <strong>{questionCount} questions</strong> · <strong>{selected}</strong> · <strong>{timeLimit} min</strong> · ~{Math.round((timeLimit * 60) / questionCount)}s per question
                    </p>
                </div>
            )}

            <Button
                onClick={handleStart}
                disabled={!selected || loading}
                size="lg"
                className="bg-indigo-600 hover:bg-indigo-700 text-white w-full sm:w-auto px-10"
                data-testid="start-exam-btn"
            >
                {loading
                    ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Starting...</>
                    : <>Start Exam <ChevronRight className="ml-2 h-4 w-4" /></>
                }
            </Button>
        </div>
    );
}

// ── Exam Screen ────────────────────────────────────────────────────────────────
function ExamScreen({ sessionId, organSystem, examType, totalQuestions, timeLimitSeconds, onFinish, isDark }) {
    const [question, setQuestion] = useState(null);
    const [progress, setProgress] = useState({ current: 0, total: totalQuestions });
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(true);
    const [timeLeft, setTimeLeft] = useState(timeLimitSeconds);
    const [answers, setAnswers] = useState([]);
    const timerRef = useRef(null);
    const colors = getColor(organSystem, examType);

    // Countdown timer
    useEffect(() => {
        timerRef.current = setInterval(() => {
            setTimeLeft(t => {
                if (t <= 1) {
                    clearInterval(timerRef.current);
                    handleTimeUp();
                    return 0;
                }
                return t - 1;
            });
        }, 1000);
        return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleTimeUp = useCallback(async () => {
        toast.warning('Time is up! Submitting exam...');
        try {
            const res = await shelfAPI.submitExam(sessionId, answers, timeLimitSeconds);
            onFinish(res.data);
        } catch {
            toast.error('Failed to submit exam');
        }
    }, [sessionId, answers, timeLimitSeconds, onFinish]);

    const loadQuestion = useCallback(async () => {
        setLoading(true);
        setSelectedAnswer(null);
        setSubmitted(false);
        setResult(null);
        try {
            const res = await shelfAPI.getQuestion(sessionId);
            if (res.data.status === 'completed') {
                clearInterval(timerRef.current);
                const elapsed = timeLimitSeconds - timeLeft;
                const finalRes = await shelfAPI.submitExam(sessionId, answers, elapsed);
                onFinish(finalRes.data);
            } else {
                setQuestion(res.data.question);
                setProgress(res.data.progress);
            }
        } catch (err) {
            toast.error('Failed to load question');
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId]);

    useEffect(() => { loadQuestion(); }, [loadQuestion]);

    const handleSubmit = async () => {
        if (!selectedAnswer) { toast.error('Please select an answer'); return; }
        setSubmitted(true);
        try {
            const res = await shelfAPI.submitAnswer(sessionId, question.id, selectedAnswer);
            setResult(res.data);
            setAnswers(prev => [...prev, { question_id: question.id, selected_answer: selectedAnswer }]);
        } catch (err) {
            const detail = err?.response?.data?.detail || err?.message || 'Unknown error';
            toast.error(`Failed to submit answer: ${detail}`);
        }
    };

    const handleNext = () => {
        if (progress.current >= progress.total) {
            clearInterval(timerRef.current);
            const elapsed = timeLimitSeconds - timeLeft;
            shelfAPI.submitExam(sessionId, answers, elapsed).then(r => onFinish(r.data)).catch(() => toast.error('Failed to submit'));
        } else {
            loadQuestion();
        }
    };

    const isLowTime = timeLeft < 120;

    return (
        <div className="max-w-3xl mx-auto space-y-5">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Badge className={cn("text-sm px-3 py-1", colors.bg, "text-white border-0")}>
                        {examType === 'subject'
                            ? <GraduationCap className="h-3.5 w-3.5 mr-1.5" />
                            : <Activity className="h-3.5 w-3.5 mr-1.5" />
                        }
                        {organSystem}
                    </Badge>
                    <span className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>
                        Q{progress.current}/{progress.total}
                    </span>
                </div>
                <div className={cn(
                    "flex items-center gap-2 px-3 py-1.5 rounded-full font-mono font-bold text-sm",
                    isLowTime
                        ? isDark ? "bg-red-900/50 text-red-300" : "bg-red-100 text-red-700"
                        : isDark ? "bg-slate-700 text-slate-200" : "bg-slate-100 text-slate-700"
                )} data-testid="timer">
                    <Clock className="h-4 w-4" />
                    {formatTime(timeLeft)}
                </div>
            </div>

            <Progress value={(progress.current / progress.total) * 100} className="h-1.5" />

            {loading ? (
                <div className="flex flex-col items-center justify-center py-20 gap-4">
                    <Loader2 className={cn("h-10 w-10 animate-spin", isDark ? "text-indigo-400" : "text-indigo-600")} />
                    <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>Generating question...</p>
                </div>
            ) : question ? (
                <Card className={cn("border", isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200")}>
                    <CardContent className="p-6 space-y-6">
                        {/* Vignette */}
                        <div className={cn(
                            "p-4 rounded-lg border-l-4",
                            isDark ? "bg-slate-900/50 border-indigo-500 text-slate-200" : "bg-indigo-50 border-indigo-400 text-slate-700"
                        )}>
                            <p className="text-sm leading-relaxed">{question.vignette}</p>
                        </div>

                        {/* Question stem */}
                        <p className={cn("font-semibold text-base leading-snug", isDark ? "text-white" : "text-slate-900")} style={{ fontFamily: 'Manrope' }}>
                            {question.question_stem}
                        </p>

                        {/* Answer options */}
                        <div className="space-y-2.5">
                            {question.options.map(opt => {
                                const isCorrect = submitted && opt.letter === result?.correct_answer;
                                const isWrong = submitted && opt.letter === selectedAnswer && !result?.is_correct;
                                return (
                                    <button
                                        key={opt.letter}
                                        disabled={submitted}
                                        onClick={() => setSelectedAnswer(opt.letter)}
                                        className={cn(
                                            "w-full flex items-start gap-3 p-3.5 rounded-xl border-2 text-left transition-all",
                                            !submitted && selectedAnswer === opt.letter && "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/40",
                                            !submitted && selectedAnswer !== opt.letter && (isDark ? "border-slate-600 hover:border-slate-500" : "border-slate-200 hover:border-slate-300"),
                                            isCorrect && (isDark ? "border-emerald-500 bg-emerald-900/50" : "border-emerald-500 bg-emerald-50"),
                                            isWrong && (isDark ? "border-red-500 bg-red-900/50" : "border-red-500 bg-red-50"),
                                            submitted && !isCorrect && !isWrong && "opacity-50"
                                        )}
                                        data-testid={`option-${opt.letter}`}
                                    >
                                        <span className={cn(
                                            "flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold",
                                            isCorrect ? "bg-emerald-500 text-white"
                                                : isWrong ? "bg-red-500 text-white"
                                                : selectedAnswer === opt.letter && !submitted ? "bg-indigo-500 text-white"
                                                : isDark ? "bg-slate-700 text-slate-300" : "bg-slate-100 text-slate-600"
                                        )}>
                                            {opt.letter}
                                        </span>
                                        <span className={cn(
                                            "text-sm flex-1 pt-0.5",
                                            isCorrect && (isDark ? "text-emerald-200" : "text-emerald-900"),
                                            isWrong && (isDark ? "text-red-200" : "text-red-900"),
                                            !isCorrect && !isWrong && (isDark ? "text-slate-200" : "text-slate-700")
                                        )}>
                                            {opt.text}
                                        </span>
                                        {isCorrect && <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Explanation */}
                        {submitted && result && (
                            <div className={cn(
                                "p-4 rounded-xl border-2",
                                result.is_correct
                                    ? isDark ? "bg-emerald-900/40 border-emerald-600" : "bg-emerald-50 border-emerald-300"
                                    : isDark ? "bg-amber-900/40 border-amber-600" : "bg-amber-50 border-amber-300"
                            )}>
                                <div className="flex items-center gap-2 mb-2">
                                    <BookOpen className={cn("h-4 w-4", result.is_correct ? "text-emerald-500" : "text-amber-500")} />
                                    <span className={cn("font-semibold text-sm",
                                        result.is_correct ? isDark ? "text-emerald-300" : "text-emerald-800" : isDark ? "text-amber-300" : "text-amber-800"
                                    )}>
                                        {result.is_correct ? 'Correct!' : 'Explanation'}
                                    </span>
                                    {result.concept && (
                                        <Badge variant="outline" className="ml-auto text-xs">{result.concept}</Badge>
                                    )}
                                </div>
                                <p className={cn("text-sm leading-relaxed",
                                    result.is_correct ? isDark ? "text-emerald-200" : "text-emerald-900" : isDark ? "text-amber-200" : "text-amber-900"
                                )}>
                                    {result.explanation}
                                </p>
                            </div>
                        )}

                        {/* Actions */}
                        <div className="flex justify-end gap-3">
                            {!submitted ? (
                                <Button
                                    onClick={handleSubmit}
                                    disabled={!selectedAnswer}
                                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-8"
                                    data-testid="submit-answer-btn"
                                >
                                    Submit Answer
                                </Button>
                            ) : (
                                <Button
                                    onClick={handleNext}
                                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-8"
                                    data-testid="next-question-btn"
                                >
                                    {progress.current >= progress.total ? 'Finish Exam' : 'Next Question'}
                                    <ChevronRight className="ml-2 h-4 w-4" />
                                </Button>
                            )}
                        </div>
                    </CardContent>
                </Card>
            ) : null}
        </div>
    );
}

// ── Results Screen ─────────────────────────────────────────────────────────────
function ResultsScreen({ results, isDark, onRetry, onBack }) {
    const passed = results.score_percentage >= 60;
    const colors = getColor(results.organ_system, results.exam_type || 'system');

    return (
        <div className="max-w-2xl mx-auto space-y-6">
            {/* Score card */}
            <div className={cn(
                "rounded-2xl p-8 text-center",
                passed
                    ? isDark ? "bg-emerald-900/30 border border-emerald-700" : "bg-emerald-50 border border-emerald-200"
                    : isDark ? "bg-red-900/30 border border-red-700" : "bg-red-50 border border-red-200"
            )}>
                <Trophy className={cn("h-12 w-12 mx-auto mb-4", passed ? "text-emerald-500" : "text-red-400")} />
                <p className={cn("text-5xl font-black mb-2", isDark ? "text-white" : "text-slate-900")} style={{ fontFamily: 'Manrope' }}>
                    {results.score_percentage}%
                </p>
                <p className={cn("text-lg font-semibold mb-1", passed ? "text-emerald-600" : "text-red-600")}>
                    {passed ? 'Passed' : 'Needs Improvement'}
                </p>
                <Badge className={cn("text-sm", colors.bg, "text-white border-0")}>
                    {results.exam_type === 'subject'
                        ? <GraduationCap className="h-3.5 w-3.5 mr-1.5" />
                        : <Activity className="h-3.5 w-3.5 mr-1.5" />
                    }
                    {results.organ_system}
                </Badge>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
                {[
                    { label: 'Correct', value: results.correct_answers, color: 'text-emerald-600' },
                    { label: 'Incorrect', value: results.incorrect_answers, color: 'text-red-500' },
                    { label: 'Time', value: formatTime(results.time_spent_seconds), color: 'text-indigo-600' },
                ].map(s => (
                    <div key={s.label} className={cn("rounded-xl p-4 text-center", isDark ? "bg-slate-800 border border-slate-700" : "bg-white border border-slate-200")}>
                        <p className={cn("text-2xl font-bold", s.color)}>{s.value}</p>
                        <p className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>{s.label}</p>
                    </div>
                ))}
            </div>

            {/* Pass marker */}
            {!passed && (
                <div className={cn("flex items-center gap-2 p-3 rounded-lg text-sm", isDark ? "bg-amber-900/30 text-amber-200" : "bg-amber-50 text-amber-800")}>
                    <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                    Passing score is 60%. Review {results.organ_system} concepts and try again.
                </div>
            )}

            {/* Category Breakdown Bar Chart */}
            {results.category_breakdown && Object.keys(results.category_breakdown).length > 0 && (
                <div className={cn("rounded-xl p-5 border", isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200")} data-testid="category-breakdown">
                    <h3 className={cn("text-sm font-bold mb-4 flex items-center gap-2", isDark ? "text-white" : "text-slate-900")} style={{ fontFamily: 'Manrope' }}>
                        <BarChart3 className="h-4 w-4 text-indigo-500" />
                        Performance by Category
                    </h3>
                    <div className="space-y-3">
                        {Object.entries(results.category_breakdown)
                            .sort(([,a], [,b]) => b.total - a.total)
                            .map(([category, stats]) => {
                                const pct = stats.percentage;
                                const barColor = pct >= 70 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-500';
                                return (
                                    <div key={category}>
                                        <div className="flex items-center justify-between mb-1">
                                            <span className={cn("text-xs font-medium truncate mr-2", isDark ? "text-slate-300" : "text-slate-700")}>
                                                {category}
                                            </span>
                                            <span className={cn("text-xs font-bold whitespace-nowrap", isDark ? "text-slate-200" : "text-slate-900")}>
                                                {stats.correct}/{stats.total} ({pct}%)
                                            </span>
                                        </div>
                                        <div className={cn("h-5 rounded-full overflow-hidden", isDark ? "bg-slate-700" : "bg-slate-100")}>
                                            <div
                                                className={cn("h-full rounded-full transition-all duration-700", barColor)}
                                                style={{ width: `${Math.max(pct, 2)}%` }}
                                            />
                                        </div>
                                    </div>
                                );
                            })
                        }
                    </div>
                    <div className="flex items-center gap-4 mt-4 pt-3 border-t border-slate-200">
                        <div className="flex items-center gap-1.5 text-xs">
                            <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                            <span className={isDark ? "text-slate-400" : "text-slate-500"}>70%+</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs">
                            <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                            <span className={isDark ? "text-slate-400" : "text-slate-500"}>50-69%</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs">
                            <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                            <span className={isDark ? "text-slate-400" : "text-slate-500"}>Below 50%</span>
                        </div>
                    </div>
                </div>
            )}

            <div className="flex gap-3">
                <Button variant="outline" onClick={onBack} className={cn("flex-1", isDark && "border-slate-600 text-slate-200")}>
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    New Exam
                </Button>
                <Button onClick={onRetry} className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white">
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Try Again
                </Button>
            </div>
        </div>
    );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function ShelfExam() {
    const { isDark } = useTheme();
    const navigate = useNavigate();
    const [phase, setPhase] = useState('setup'); // setup | exam | results
    const [sessionId, setSessionId] = useState(null);
    const [organSystem, setOrganSystem] = useState(null);
    const [examType, setExamType] = useState('subject');
    const [totalQuestions, setTotalQuestions] = useState(40);
    const [timeLimitSeconds, setTimeLimitSeconds] = useState(3600);
    const [results, setResults] = useState(null);

    const handleStart = (sid, sys, count, timeSeconds, type) => {
        setSessionId(sid);
        setOrganSystem(sys);
        setExamType(type);
        setTotalQuestions(count);
        setTimeLimitSeconds(timeSeconds);
        setPhase('exam');
    };

    const handleFinish = (res) => {
        setResults({ ...res, exam_type: examType });
        setPhase('results');
    };

    return (
        <Layout>
            <div className="py-4" data-testid="shelf-exam-page">
                {phase === 'setup' && <SetupScreen onStart={handleStart} isDark={isDark} />}
                {phase === 'exam' && sessionId && (
                    <ExamScreen
                        sessionId={sessionId}
                        organSystem={organSystem}
                        examType={examType}
                        totalQuestions={totalQuestions}
                        timeLimitSeconds={timeLimitSeconds}
                        onFinish={handleFinish}
                        isDark={isDark}
                    />
                )}
                {phase === 'results' && results && (
                    <ResultsScreen
                        results={results}
                        isDark={isDark}
                        onRetry={() => setPhase('setup')}
                        onBack={() => setPhase('setup')}
                    />
                )}
            </div>
        </Layout>
    );
}
